import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useTheme, useThemeClasses } from "../providers/ThemeProvider";
import ShiftSelector from "./ShiftSelector";
import Tabs from "./Tabs";
import DateRangeField from "./DateRangeField";
import EfficiencyTab from "./tabs/EfficiencyTab";
import OutputTab from "./tabs/OutputTab";
import DowntimeTab from "./tabs/DowntimeTab";
import RejectsTab from "./tabs/RejectsTab";
import MTBFTab from "./tabs/MTBFTab";
import FaultsTab from "./tabs/FaultsTab";
import { styles } from "../styles";
import type { Machine } from "../services/BackendDataService";

// KPI tab definitions
const kpiTabs = [
  "Efficiency",
  "Output",
  "Downtime",
  "Rejects",
  "MTBF",
  "Faults",
] as const;
const kpiTabKeys = [
  "efficiency",
  "output",
  "downtime",
  "rejects",
  "mtbf",
  "faults",
] as const;

type KpiTab = (typeof kpiTabs)[number];

interface LineOverviewProps {
  lineId: string;
  machines: Machine[];
}

/**
 * LineOverview - Overview component for Line view
 *
 * This component displays KPI tabs for a production line.
 * Uses line-specific NATS requests - backend aggregates data from all machines in the line.
 */
const LineOverview = ({ lineId, machines }: LineOverviewProps) => {
  const { t } = useTranslation();
  const { currentTheme } = useTheme();
  const themeClasses = useThemeClasses();

  // State for shift selector and KPI tabs
  const [shifts, setShifts] = useState<string[]>(["A"]);
  const [activeKpiTab, setActiveKpiTab] = useState<KpiTab>("Efficiency");
  const [hasAvailableShifts, setHasAvailableShifts] = useState(false);
  const [shiftsLoading, setShiftsLoading] = useState(true);

  const handleAvailabilityChange = useCallback(
    (available: boolean, loading: boolean) => {
      setHasAvailableShifts(available);
      setShiftsLoading(loading);
    },
    [],
  );

  // Determine display states
  const noMachines = machines.length === 0;
  const noShiftsAvailable =
    !shiftsLoading && !hasAvailableShifts && !noMachines;
  const noShiftsSelected =
    !shiftsLoading && hasAvailableShifts && shifts.length === 0;
  const shouldShowCharts =
    !shiftsLoading && hasAvailableShifts && shifts.length > 0;

  // Render KPI tab content - pass lineId for line-level aggregated data
  let TabContent = null;
  if (shouldShowCharts) {
    switch (activeKpiTab) {
      case "Efficiency":
        TabContent = <EfficiencyTab lineId={lineId} shifts={shifts} />;
        break;
      case "Output":
        TabContent = <OutputTab lineId={lineId} shifts={shifts} />;
        break;
      case "Downtime":
        TabContent = <DowntimeTab lineId={lineId} shifts={shifts} />;
        break;
      case "Rejects":
        TabContent = <RejectsTab lineId={lineId} shifts={shifts} />;
        break;
      case "MTBF":
        TabContent = <MTBFTab lineId={lineId} shifts={shifts} />;
        break;
      case "Faults":
        TabContent = <FaultsTab lineId={lineId} shifts={shifts} />;
        break;
      default:
        TabContent = null;
    }
  }

  return (
    <main
      className={styles.overview.container}
      style={{
        backgroundColor: currentTheme.isDark
          ? "var(--bg-secondary)"
          : "#f3f4f6",
        transition: "background-color 0.3s ease",
      }}
    >
      {/* Date range + shift selector on one row */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <DateRangeField />
        <div className="flex-shrink-0">
          <ShiftSelector
            shifts={shifts}
            setShifts={setShifts}
            lineId={lineId}
            className={styles.overview.shiftSelector.wrapper}
            onAvailabilityChange={handleAvailabilityChange}
          />
        </div>
      </div>

      {/* KPI Tabs */}
      <Tabs
        tabs={kpiTabKeys.map((key) => t(key))}
        activeTab={t(activeKpiTab.toLowerCase())}
        setActiveTab={(translatedTab) => {
          // Find index of the translated tab in the translated tabs array
          const translatedTabs = kpiTabKeys.map((key) => t(key));
          const index = translatedTabs.findIndex(
            (tab) => tab.toLowerCase() === translatedTab.toLowerCase(),
          );
          if (index >= 0) setActiveKpiTab(kpiTabs[index]);
        }}
      />

      {/* Content Area */}
      <div className={styles.overview.content.container}>
        {/* Message when no machines */}
        {noMachines && (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="text-6xl mb-4">⚙️</div>
            <h3
              className={`text-xl font-semibold mb-2 ${themeClasses.textPrimary}`}
            >
              {t("noMachinesInLine", "No machines in this line")}
            </h3>
            <p className={`text-center max-w-md ${themeClasses.textSecondary}`}>
              {t(
                "noMachinesInLineMessage",
                "This line has no machines with data available.",
              )}
            </p>
          </div>
        )}

        {/* Message when no shifts available */}
        {noShiftsAvailable && (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="text-6xl mb-4">📅</div>
            <h3
              className={`text-xl font-semibold mb-2 ${themeClasses.textPrimary}`}
            >
              {t("noShiftsInTimeframe", "No shifts in selected timeframe")}
            </h3>
            <p className={`text-center max-w-md ${themeClasses.textSecondary}`}>
              {t(
                "selectDifferentTimeframe",
                "Try selecting a different date range where this line has production data.",
              )}
            </p>
          </div>
        )}

        {/* Message when no shifts selected */}
        {noShiftsSelected && (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="text-6xl mb-4">☑️</div>
            <h3
              className={`text-xl font-semibold mb-2 ${themeClasses.textPrimary}`}
            >
              {t("noShiftSelected", "No shift selected")}
            </h3>
            <p className={`text-center max-w-md ${themeClasses.textSecondary}`}>
              {t(
                "pleaseSelectShift",
                "Please select at least one shift from the shift selector above to view the charts.",
              )}
            </p>
          </div>
        )}

        {/* Show charts */}
        {shouldShowCharts && TabContent}
      </div>
    </main>
  );
};

export default LineOverview;
